import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Mail, CheckCircle2, XCircle, Eye, Settings, Send, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";

interface EmailPreferences {
  onboarding_emails: boolean;
  waitlist_emails: boolean;
  admin_emails: boolean;
  digest_emails: boolean;
  content_recommendations: boolean;
  marketing_emails: boolean;
  digest_frequency: "daily" | "weekly" | "monthly" | "never";
  recommendation_frequency: "weekly" | "biweekly" | "monthly" | "never";
}

const emailTypes = [
  {
    id: "welcome",
    name: "Welcome Email",
    category: "onboarding_emails",
    description: "Sent when user account is approved",
    icon: "👋",
  },
  {
    id: "project_reminder",
    name: "Project Reminder",
    category: "onboarding_emails",
    description: "Sent 24h after board creation if no project created",
    icon: "📋",
  },
  {
    id: "session_reminder",
    name: "Session Reminder",
    category: "onboarding_emails",
    description: "Sent 12h after project creation if no session started",
    icon: "💬",
  },
  {
    id: "daily_digest",
    name: "Daily Digest",
    category: "digest_emails",
    frequency: "digest_frequency",
    description: "Daily summary of waitlist activity (admin only)",
    icon: "📊",
  },
  {
    id: "weekly_content_recommendations",
    name: "Weekly Content Recommendations",
    category: "content_recommendations",
    frequency: "recommendation_frequency",
    description: "Weekly content gap analysis (admin only)",
    icon: "💡",
  },
  {
    id: "thank_you",
    name: "Thank You (Email Verified)",
    category: "waitlist_emails",
    description: "Sent when user verifies email (waitlist)",
    icon: "✅",
  },
  {
    id: "takeout_ready",
    name: "Data Takeout Ready",
    category: "onboarding_emails",
    description: "Sent when data export is ready",
    icon: "📦",
  },
  {
    id: "admin_security_alert",
    name: "Admin Security Alert",
    category: "admin_emails",
    description: "Alerts admins about security events",
    icon: "🛡️",
  },
  {
    id: "approval",
    name: "Account Approval",
    category: "onboarding_emails",
    description: "Sent to user when their account is approved",
    icon: "🎉",
  },
  {
    id: "magic_link",
    name: "Magic Link",
    category: "onboarding_emails",
    description: "Sent for passwordless login",
    icon: "✨",
  },
  {
    id: "new_waitlist_user",
    name: "New Waitlist User (Admin)",
    category: "admin_emails",
    description: "Notifies admins of a new waitlist signup",
    icon: "🔔",
  },
  {
    id: "password_reset",
    name: "Password Reset",
    category: "onboarding_emails",
    description: "Sent when a user requests to reset their password",
    icon: "🔑",
  },
  {
    id: "user_security_alert",
    name: "User Security Alert",
    category: "onboarding_emails",
    description: "Alerts user about suspicious activity",
    icon: "🚨",
  },
  {
    id: "verification",
    name: "Email Verification",
    category: "onboarding_emails",
    description: "Initial email to verify a new user's address",
    icon: "📧",
  },
  {
    id: "verification_reminder",
    name: "Verification Reminder",
    category: "onboarding_emails",
    description: "Reminder to verify email address",
    icon: "⏰",
  },
  {
    id: "weekly_momentum_digest",
    name: "Weekly Momentum Digest",
    category: "digest_emails",
    frequency: "digest_frequency",
    description: "Weekly summary of project momentum",
    icon: "📈",
  },
];

export default function EmailPreviewer() {
  const [preferences, setPreferences] = useState<EmailPreferences>({
    onboarding_emails: true,
    waitlist_emails: true,
    admin_emails: true,
    digest_emails: true,
    content_recommendations: true,
    marketing_emails: false,
    digest_frequency: "weekly",
    recommendation_frequency: "weekly",
  });

  const [selectedEmail, setSelectedEmail] = useState<string>(emailTypes[0].id);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const { execute: invokeEdgeFunction, isLoading: isInvoking } = useEdgeFunction({ auth: true });

  const { data: previewData, isLoading: isLoadingPreview } = useQuery({
    queryKey: ["email-preview", selectedEmail],
    queryFn: async () => {
      if (!selectedEmail) return null;
      const { data, error } = await invokeEdgeFunction("preview-email-template", {
        body: { templateName: selectedEmail },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!selectedEmail,
    staleTime: Infinity,
  });

  const wouldReceiveEmail = (emailType: typeof emailTypes[0]): boolean => {
    const categoryEnabled = preferences[emailType.category as keyof EmailPreferences];
    
    if (!categoryEnabled) return false;

    // Check frequency for digest and recommendation emails
    if (emailType.frequency) {
      const frequencyValue = preferences[emailType.frequency as keyof EmailPreferences];
      return frequencyValue !== "never";
    }

    return true;
  };

  const updatePreference = (key: keyof EmailPreferences, value: any) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
  };

  const sendTestEmail = async () => {
    if (!selectedEmailData) return;

    setIsSendingTest(true);
    try {
      const { error } = await invokeEdgeFunction("send-test-email", {
        body: {
          templateName: selectedEmail,
        },
        showErrorToast: true,
      });

      if (error) {
        throw error;
      }

      toast.success(`Test email "${selectedEmailData.name}" sent to your email address!`);
    } catch (error: any) {
      console.error("Error sending test email:", error);
      // Error toast already shown by hook if showErrorToast is true
    } finally {
      setIsSendingTest(false);
    }
  };

  const selectedEmailData = emailTypes.find((e) => e.id === selectedEmail);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Email Previewer & Tester
        </CardTitle>
        <CardDescription>
          Preview different email types and test how preference settings affect delivery
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="preview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="preview">Email Preview</TabsTrigger>
            <TabsTrigger value="preferences">Test Preferences</TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="space-y-4">
            <div>
              <Label>Select Email Type</Label>
              <Select value={selectedEmail} onValueChange={setSelectedEmail}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {emailTypes.map((email) => (
                    <SelectItem key={email.id} value={email.id}>
                      <div className="flex items-center gap-2">
                        <span>{email.icon}</span>
                        <span>{email.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedEmailData && (
              <>
                <Alert>
                  <Mail className="h-4 w-4" />
                  <AlertDescription>
                    <div className="flex items-center justify-between">
                      <span>{selectedEmailData.description}</span>
                      {wouldReceiveEmail(selectedEmailData) ? (
                        <Badge variant="default" className="ml-2">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Would Receive
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="ml-2">
                          <XCircle className="h-3 w-3 mr-1" />
                          Would Block
                        </Badge>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>

                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      <span className="font-medium">Email Preview</span>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={sendTestEmail}
                      disabled={isSendingTest || isInvoking}
                    >
                      {isSendingTest ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Test Email
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="bg-background border rounded overflow-auto max-h-96 h-[500px]">
                    {isLoadingPreview ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : previewData?.html ? (
                      <iframe srcDoc={previewData.html} className="w-full h-full border-0" title="Email Preview" />
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    The test email will be sent to your email address
                  </p>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="preferences" className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="onboarding">Onboarding Emails</Label>
                <Switch
                  id="onboarding"
                  checked={preferences.onboarding_emails}
                  onCheckedChange={(checked) => updatePreference("onboarding_emails", checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="waitlist">Waitlist Emails</Label>
                <Switch
                  id="waitlist"
                  checked={preferences.waitlist_emails}
                  onCheckedChange={(checked) => updatePreference("waitlist_emails", checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="admin">Admin Emails</Label>
                <Switch
                  id="admin"
                  checked={preferences.admin_emails}
                  onCheckedChange={(checked) => updatePreference("admin_emails", checked)}
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="digest">Digest Emails</Label>
                  <Switch
                    id="digest"
                    checked={preferences.digest_emails}
                    onCheckedChange={(checked) => updatePreference("digest_emails", checked)}
                  />
                </div>
                {preferences.digest_emails && (
                  <div className="ml-4">
                    <Label htmlFor="digest-freq" className="text-sm text-muted-foreground">
                      Digest Frequency
                    </Label>
                    <Select
                      value={preferences.digest_frequency}
                      onValueChange={(value) => updatePreference("digest_frequency", value)}
                    >
                      <SelectTrigger id="digest-freq">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="never">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="recommendations">Content Recommendations</Label>
                  <Switch
                    id="recommendations"
                    checked={preferences.content_recommendations}
                    onCheckedChange={(checked) => updatePreference("content_recommendations", checked)}
                  />
                </div>
                {preferences.content_recommendations && (
                  <div className="ml-4">
                    <Label htmlFor="rec-freq" className="text-sm text-muted-foreground">
                      Recommendation Frequency
                    </Label>
                    <Select
                      value={preferences.recommendation_frequency}
                      onValueChange={(value) => updatePreference("recommendation_frequency", value)}
                    >
                      <SelectTrigger id="rec-freq">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="never">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <Label htmlFor="marketing">Marketing Emails</Label>
                <Switch
                  id="marketing"
                  checked={preferences.marketing_emails}
                  onCheckedChange={(checked) => updatePreference("marketing_emails", checked)}
                />
              </div>
            </div>

            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium mb-2">Email Delivery Summary:</p>
                <div className="space-y-1 text-sm">
                  {emailTypes.map((email) => (
                    <div key={email.id} className="flex items-center justify-between">
                      <span>
                        {email.icon} {email.name}
                      </span>
                      {wouldReceiveEmail(email) ? (
                        <Badge variant="default" className="text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Receive
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          <XCircle className="h-3 w-3 mr-1" />
                          Block
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
